import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Radio, Users, FileText, Building2, MessageCircle, RefreshCw, AlertTriangle } from "lucide-react";

export default function AdminWarRoom() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/war-room"); setData(r.data); }
    catch { /* silent — polling */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    // Scroll ao topo ao abrir — a Sala de Guerra vinha herdando o scroll
    // da página anterior (ex.: rodapé do /admin) e o usuário via a página
    // já rolada e o KPI/plantão "escondido" acima. Reset explícito.
    window.scrollTo(0, 0);
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Carregando Sala de Guerra…</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-red-600">Erro ao carregar</div>;

  const riskClass = { emergencial: "text-red-600", alta: "text-orange-600", moderada: "text-amber-600", programado: "text-green-600" };

  return (
    <div className="min-h-screen bg-slate-900 text-white p-3 sm:p-6" data-testid="war-room-page">
      <div className="flex items-center justify-between mb-4 sm:mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-red-400 text-xs uppercase tracking-[0.18em]">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> Ao vivo · atualiza a cada 20s
          </div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl md:text-4xl text-white mt-1">Sala de Guerra RAF</h1>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <button onClick={load} data-testid="war-refresh" className="px-3 py-1.5 border border-slate-700 rounded-md text-xs hover:bg-slate-800 inline-flex items-center gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" /> Atualizar
          </button>
          <Link to="/admin" className="text-xs text-slate-400 hover:text-white">Voltar</Link>
        </div>
      </div>

      {/* KPIs — coluna única no mobile */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-4 sm:mb-6">
        <Kpi icon={Radio} label="Em plantão" value={data.on_call_count} color="text-red-400" />
        <Kpi icon={FileText} label="Casos abertos" value={data.open_total} color="text-amber-400" />
        <Kpi icon={AlertTriangle} label="🔴 Emergenciais" value={data.by_risk.emergencial} color="text-red-500" />
        <Kpi icon={Building2} label="Certs a vencer (30d)" value={data.expiring_certs.length} color="text-orange-400" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {/* On-Call */}
        <Section title="Plantão ao vivo" icon={Radio} count={data.on_call.length}>
          {data.on_call.length === 0 ? (
            <div className="text-slate-500 text-sm p-4">Ninguém em plantão agora.</div>
          ) : (
            <div className="divide-y divide-slate-700">
              {data.on_call.map((u) => (
                <div key={u.id} data-testid={`war-oncall-${u.id}`} className="p-3 flex items-center gap-3">
                  {u.avatar_url ? (
                    <img src={u.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-sm font-bold">{(u.name || "?")[0]}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-white truncate">{u.name}</div>
                    <div className="text-xs text-slate-400">Desde {u.on_call_since ? new Date(u.on_call_since).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "—"}</div>
                  </div>
                  {u.whatsapp_link && (
                    <a href={u.whatsapp_link} target="_blank" rel="noreferrer"
                       data-testid={`war-wa-${u.id}`}
                       className="p-2 rounded-md bg-green-600 hover:bg-green-500">
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Casos abertos */}
        <Section title="Casos abertos" icon={FileText} count={data.open_total}>
          {data.open_cases.length === 0 ? (
            <div className="text-slate-500 text-sm p-4">Nenhum caso aberto.</div>
          ) : (
            <div className="divide-y divide-slate-700 max-h-96 overflow-y-auto">
              {data.open_cases.map((c) => (
                <Link
                  key={c.id}
                  to={`/consultor/caso/${c.id}`}
                  data-testid={`war-case-${c.id}`}
                  className="p-3 flex items-center gap-3 hover:bg-slate-800 transition-colors"
                >
                  <div className={`text-xs font-bold uppercase ${riskClass[c.risk_level] || "text-slate-400"}`}>
                    {(c.risk_level || "?").slice(0, 4)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs text-slate-500">{c.protocol}</div>
                    <div className="text-sm truncate">{c.patient_name} · {c.city}</div>
                    {c.consultant_name ? (
                      <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1">
                        {c.consultant_on_call && <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" title="Em plantão" />}
                        Consultor: <span className="text-slate-300 font-semibold">{c.consultant_name}</span>
                      </div>
                    ) : (
                      <div className="text-[10px] text-amber-400 mt-0.5">Sem consultor atribuído</div>
                    )}
                  </div>
                  <div className="text-xs text-slate-400">{c.status}</div>
                </Link>
              ))}
            </div>
          )}
        </Section>

        {/* Próximos plantões */}
        <Section title="Próximos plantões (6h)" icon={Users} count={data.upcoming_slots.length}>
          {data.upcoming_slots.length === 0 ? (
            <div className="text-slate-500 text-sm p-4">Nenhum slot nas próximas 6h.</div>
          ) : (
            <div className="divide-y divide-slate-700">
              {data.upcoming_slots.map((s) => (
                <div key={s.id} className="p-3 flex items-center justify-between text-sm">
                  <span>{String(s.start_hour).padStart(2, "0")}:00 – {String(s.end_hour).padStart(2, "0")}:00</span>
                  <span className="text-slate-400 text-xs">{s.consultor_name || s.consultor_id.slice(0, 8)}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Cert alerts */}
        <Section title="Certs a vencer" icon={Building2} count={data.expiring_certs.length}>
          {data.expiring_certs.length === 0 ? (
            <div className="text-slate-500 text-sm p-4">Nenhum certificado vencendo nos próximos 30 dias.</div>
          ) : (
            <div className="divide-y divide-slate-700">
              {data.expiring_certs.map((c) => (
                <div key={c.id} data-testid={`war-cert-${c.id}`} className="p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{c.name}</div>
                    <div className="text-xs text-slate-400">{c.city}/{c.state} · vence em {c.days_left} dia(s)</div>
                  </div>
                  {c.whatsapp_link && (
                    <a href={c.whatsapp_link} target="_blank" rel="noreferrer"
                       className="p-2 rounded-md bg-green-600 hover:bg-green-500">
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, color }) => (
  <div className="p-4 bg-slate-800 rounded-xl border border-slate-700">
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
      <Icon className={`w-4 h-4 ${color}`} /> {label}
    </div>
    <div className={`font-display font-bold text-4xl mt-1 ${color}`}>{value}</div>
  </div>
);

const Section = ({ title, icon: Icon, count, children }) => (
  <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
    <div className="p-3 border-b border-slate-700 flex items-center gap-2">
      <Icon className="w-4 h-4 text-slate-400" />
      <h2 className="font-display font-bold text-sm text-white">{title}</h2>
      <span className="text-[10px] font-bold text-slate-400">{count}</span>
    </div>
    {children}
  </div>
);
